import React, { useEffect, useState } from 'react';
import { admin, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Flag, User, Ban, Check, ShieldAlert, RefreshCw } from 'lucide-react';

export default function AdminDatingReportsPanel() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setRows(null);
    try { setRows(await admin.listDatingReports(status)); }
    catch { toast({ title: 'Failed to load reports' }); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async (r, action) => {
    const label = action === 'ban' ? 'permanently ban' : action === 'suspend' ? 'suspend' : action === 'unsuspend' ? 'reinstate' : 'dismiss';
    if (!window.confirm(`${label[0].toUpperCase() + label.slice(1)} ${r.target.name || 'this user'} from dating?`)) return;
    setBusyId(r.id);
    try {
      await admin.resolveDatingReport(r.id, action);
      toast({ title: `Report ${action}` });
      await load();
    } catch { toast({ title: 'Action failed' }); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-dating-reports-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Flag size={18}/> Dating reports</h2>
          <p className="text-xs text-gray-400">Three unique reports against one account auto-suspend it. Reviews should happen within 24 hours.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={e => setStatus(e.target.value)}
            data-testid="admin-reports-status-filter"
            className="bg-black text-white border border-[#1f1f1f] rounded-lg px-3 py-1.5 text-sm">
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
          <button onClick={load} className="text-xs px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-300 inline-flex items-center gap-1">
            <RefreshCw size={12}/> Refresh
          </button>
        </div>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading reports…</div>
      ) : rows.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="admin-reports-empty">
          <ShieldAlert className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold text-white">No {status === 'all' ? '' : status} reports</div>
          <p className="text-sm text-gray-400 mt-1">The dating community is behaving. Nice.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-reports-list">
          {rows.map(r => (
            <li key={r.id} className="pbb-card p-4" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-black shrink-0">
                  {r.target?.photo
                    ? <img src={filesApi.urlFor(r.target.photo)} alt="" className="w-full h-full object-cover"/>
                    : <div className="w-full h-full flex items-center justify-center text-gray-500"><User size={20}/></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{r.target?.name || 'Unknown'}</span>
                    <span className="text-xs text-gray-400">{r.target?.email}</span>
                    {r.target?.datingBanned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">BANNED</span>}
                    {r.target?.datingSuspended && !r.target?.datingBanned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">SUSPENDED</span>}
                    {r.status === 'resolved' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-300 border border-gray-500/40">RESOLVED · {r.action || '—'}</span>}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Reported by <b className="text-gray-200">{r.reporter?.name || 'someone'}</b> · {new Date(r.createdAt).toLocaleString()}
                  </div>
                  <div className="mt-2 text-sm text-gray-200 whitespace-pre-wrap break-words bg-[#0d1f14] border border-[#1f1f1f] rounded-lg p-3">
                    {r.reason}
                  </div>
                </div>
                {r.status === 'pending' ? (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button onClick={() => resolve(r, 'ban')} disabled={busyId === r.id}
                      data-testid={`admin-report-ban-${r.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                      <Ban size={12}/> Ban
                    </button>
                    <button onClick={() => resolve(r, 'suspend')} disabled={busyId === r.id}
                      data-testid={`admin-report-suspend-${r.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                      Suspend
                    </button>
                    <button onClick={() => resolve(r, 'dismiss')} disabled={busyId === r.id}
                      data-testid={`admin-report-dismiss-${r.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-200 inline-flex items-center gap-1 disabled:opacity-60">
                      <Check size={12}/> Dismiss
                    </button>
                  </div>
                ) : r.target?.datingBanned || r.target?.datingSuspended ? (
                  <button onClick={() => resolve(r, 'unsuspend')} disabled={busyId === r.id}
                    data-testid={`admin-report-unsuspend-${r.id}`}
                    className="text-xs px-3 py-1.5 rounded-lg border border-green-500/50 text-green-300 hover:bg-green-500/10 inline-flex items-center gap-1 disabled:opacity-60 shrink-0">
                    Reinstate
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
